import { useCallback, type Ref } from "react";
import { cardHoloMotion, type PioneerHoloEffect } from "../lib/cardHolo";
import { useHoloCardPointer } from "../hooks/useHoloCardPointer";
import { cn } from "../lib/utils";

import "../styles/holo/holo.css";

/**
 * Default card-back art. Mirrors `PIONEER_BRAND.cardBack` (a `public/`-dir path
 * string — both apps share the repo-root `public/` via vite `publicDir`, so the
 * same path resolves in pioneer-event and web). Override via the `cardBack` prop.
 */
export const DEFAULT_HOLO_CARD_BACK = "/images/pioneer/cards/card-back.webp";

type PioneerHoloCardProps = {
  imageSrc: string;
  imageAlt: string;
  effect: PioneerHoloEffect;
  className?: string;
  fillContainer?: boolean;
  idleShimmer?: boolean;
  interactive?: boolean;
  /** Y-axis flip (deg) added to holo tilt — used by popover open animation */
  flipRotateY?: number;
  /** Popover flip animation — exposes card root for direct --rotate-x updates */
  cardRef?: Ref<HTMLDivElement | null>;
  popoverFlipping?: boolean;
  /** Multiplier on the idle shine/glare opacity (0-1) — use <1 for small thumbnails where full-intensity holo washes out the art */
  idleOpacityScale?: number;
  /** Strips the color-dodge blend / contrast-saturate filters from the shine+glare layers — use for small decorative thumbnails where the full holo treatment obscures the card art */
  muted?: boolean;
  /** Card-back image src — defaults to the shared TYCOONEX card back in `public/` */
  cardBack?: string;
  /** Front-art `<img loading>` hint — pass "lazy" from grid consumers so offscreen cards don't eager-load */
  loading?: "lazy" | "eager";
};

export function PioneerHoloCard({
  imageSrc,
  imageAlt,
  effect,
  className,
  fillContainer = false,
  idleShimmer = false,
  interactive = true,
  flipRotateY = 0,
  cardRef: externalCardRef,
  popoverFlipping = false,
  idleOpacityScale = 1,
  muted = false,
  cardBack = DEFAULT_HOLO_CARD_BACK,
  loading,
}: PioneerHoloCardProps) {
  const motion = cardHoloMotion(effect);
  const {
    cardRef: holoCardRef,
    rotatorRef,
    interacting,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
  } = useHoloCardPointer({
    idleShimmer,
    initialOpacity: motion.initialOpacity * idleOpacityScale,
    tiltDivisor: motion.tiltDivisor,
    flipRotateY,
    lockFlipRotation: popoverFlipping,
  });

  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      holoCardRef.current = node;
      if (!externalCardRef) return;
      if (typeof externalCardRef === "function") externalCardRef(node);
      else externalCardRef.current = node;
    },
    [externalCardRef, holoCardRef],
  );

  return (
    <div
      className={cn(
        "pioneer-holo-card",
        popoverFlipping && "is-popover-flipping",
        fillContainer && "fill-container",
        muted && "pioneer-holo-card--muted",
        className,
      )}
      data-holo={effect}
    >
      <div
        ref={setCardRef}
        className={cn("card interactive", interacting && "interacting")}
      >
        <div className="card__translater">
          <div
            ref={rotatorRef}
            className="card__rotator"
            onPointerDown={interactive && !idleShimmer ? onPointerDown : undefined}
            onPointerMove={interactive && !idleShimmer ? onPointerMove : undefined}
            onPointerUp={interactive && !idleShimmer ? onPointerUp : undefined}
            onPointerCancel={interactive && !idleShimmer ? onPointerCancel : undefined}
            onPointerLeave={interactive && !idleShimmer ? onPointerLeave : undefined}
          >
            {/* back face is invisible until a flip — always lazy */}
            <img
              className="card__back"
              src={cardBack}
              alt=""
              aria-hidden
              loading="lazy"
              draggable={false}
            />
            <div className="card__front">
              <img src={imageSrc} alt={imageAlt} loading={loading} draggable={false} />
              <div className="card__shine" aria-hidden />
              <div className="card__glare" aria-hidden />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
